import { FacturaVenta, FacturaVentaDetalle } from '../services/facturaVentaService';
import type {
  NotaCreditoCreateDto,
  NotaCreditoFormularioCreateDto,
  NotaCreditoItemCreateDto,
} from '../services/notaCreditoVentaService';

/**
 * Extrae NroUbicacionOrigen desde la factura (API puede devolver nroUbicacionOrigen o ubicación como "Nombre (número)").
 */
function getNroUbicacionOrigen(factura: FacturaVenta & { nroUbicacionOrigen?: number }): number | undefined {
  const nro = (factura as any).nroUbicacionOrigen;
  if (nro !== undefined && nro !== null && Number(nro) > 0) {
    return Number(nro);
  }
  const ubicacionStr = factura.ubicacion;
  if (typeof ubicacionStr === 'string' && ubicacionStr.trim()) {
    const match = ubicacionStr.match(/\((\d+)\)/);
    if (match && match[1]) {
      return Number(match[1]);
    }
  }
  return undefined;
}

/**
 * Convierte una FacturaVenta (con detalles) al payload para crear una Nota de Crédito (NotaCreditoCreateDto).
 * Incluye NroFormularioFactura para vincular la NC a la factura origen.
 */
export function facturaToNotaCreditoCreateDto(
  factura: FacturaVenta & { nroUbicacionOrigen?: number; formaEntrega?: string }
): NotaCreditoCreateDto {
  const nroUbicacionOrigen = getNroUbicacionOrigen(factura);
  const fechaEmision = factura.fechaEmision
    ? (factura.fechaEmision instanceof Date
        ? factura.fechaEmision
        : new Date(factura.fechaEmision))
    : new Date();
  const fechaEmisionISO = typeof fechaEmision.toISOString === 'function' ? fechaEmision.toISOString() : new Date().toISOString();

  const formulario: NotaCreditoFormularioCreateDto = {
    idCompania: String(factura.idCompania ?? '').trim() || '1',
    descripcion: factura.descripcion ? String(factura.descripcion).trim() : undefined,
    nroSuc: factura.nroSuc ?? undefined,
    fechaEmision: fechaEmisionISO,
    fechaVencim: fechaEmisionISO,
    nroCliente: Number(factura.nroCliente ?? 0),
    nombre: factura.nombre ? String(factura.nombre).trim() : undefined,
    nroLista: factura.nroLista ? String(factura.nroLista).trim() : undefined,
    formaDePago: factura.formaDePago ? String(factura.formaDePago).trim() : undefined,
    nroUbicacionOrigen: nroUbicacionOrigen ?? undefined,
    ordenDeCompra: factura.ordenCompra ? String(factura.ordenCompra).trim() : undefined,
    codigoDireccion: undefined,
    codigoDireccionEnvio: undefined,
    formaEntrega: (factura as any).formaEntrega ? String((factura as any).formaEntrega).trim() : undefined,
    nroFormularioFactura: factura.nroFormulario,
  };

  const items: NotaCreditoItemCreateDto[] = (factura.detalles || []).map(
    (d: FacturaVentaDetalle) => ({
      codigoArticulo: String(d.codigoArticulo ?? '').trim(),
      descripcion: d.descripcionArticulo ? String(d.descripcionArticulo).trim() : undefined,
      cantidad: Number(d.cantidad ?? 0),
      precioUnitario: Number(d.precioUnitario ?? 0),
      um: d.um ? String(d.um).trim() : undefined,
      codigoMoneda: d.codigoMoneda != null ? Number(d.codigoMoneda) : undefined,
      cotizacion: d.cotizacion != null ? Number(d.cotizacion) : undefined,
      notas: d.notas ? String(d.notas).trim() : undefined,
    })
  );

  return { formulario, items };
}

/**
 * Estado inicial del formulario de NC para la UI (encabezado + items editables).
 * Útil para precargar la pantalla y luego permitir editar antes de enviar.
 */
export interface NotaCreditoFormState {
  formulario: NotaCreditoFormularioCreateDto;
  items: NotaCreditoItemCreateDto[];
}

export function facturaToNotaCreditoFormState(
  factura: FacturaVenta & { nroUbicacionOrigen?: number; formaEntrega?: string }
): NotaCreditoFormState {
  return facturaToNotaCreditoCreateDto(factura);
}
